import React from 'react'
import styled from '@emotion/styled'
import { Table, Tooltip, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BiSemanalStore, Periodo, PeriodoRowModel } from './BiComparativoSemanal'

export function SemanalTable() {
  const { rows, loading, periodo } = BiSemanalStore.useState()

  return (
    <Table
      columns={buildColumns(periodo)}
      dataSource={rows.filter((r) => !r.isCurrent)}
      loading={loading}
      rowKey="periodStart"
      pagination={false}
      rowClassName={(r) => (r.holidayNames.length > 0 || r.annotation !== null ? 'holiday-week-row' : '')}
    />
  )
}

function buildColumns(periodo: Periodo): ColumnsType<PeriodoRowModel> {
  const periodColumn: ColumnsType<PeriodoRowModel>[number] =
    periodo === 'semana'
      ? {
          title: 'Semana',
          dataIndex: 'periodStart',
          render: (_, row) => <WeekLabel row={row} />,
        }
      : {
          title: 'Mês',
          dataIndex: 'periodStart',
          render: (periodStart: string) => formatMonthLabel(periodStart),
        }

  const workdaysColumns: ColumnsType<PeriodoRowModel> =
    periodo === 'mes' ? [{ title: 'Dias úteis', dataIndex: 'workdays' }] : []

  return [
    periodColumn,
    ...workdaysColumns,
    {
      title: 'Matrículas',
      dataIndex: 'count',
      render: (_, row) => (
        <ValueWithDelta value={row.count} previous={row.previousCount} format={String} />
      ),
    },
    {
      title: 'Receita NewDriver',
      dataIndex: 'valorNewDriverSum',
      render: (_, row) => (
        <ValueWithDelta
          value={row.valorNewDriverSum}
          previous={row.previousValorNewDriverSum}
          format={formatBRL}
        />
      ),
    },
  ]
}

function WeekLabel({ row }: { row: PeriodoRowModel }) {
  return (
    <span>
      {formatWeekLabel(row.periodStart)}
      {row.holidayNames.length > 0 && (
        <Tooltip title={row.holidayNames.join(', ')}>
          <Tag color="orange" style={{ marginLeft: 8 }}>
            Feriado
          </Tag>
        </Tooltip>
      )}
      {row.annotation !== null && (
        <Tag color="blue" style={{ marginLeft: 8 }}>
          {row.annotation}
        </Tag>
      )}
    </span>
  )
}

function ValueWithDelta({
  value,
  previous,
  format,
}: {
  value: number
  previous: number | null
  format: (v: number) => string
}) {
  const percent = previous ? ((value - previous) / previous) * 100 : null

  return (
    <span>
      {format(value)}
      {percent !== null && previous !== null && (
        <Tooltip title={`Período anterior: ${format(previous)}`}>
          <DeltaText positive={percent >= 0}>
            {percent >= 0 ? '+' : ''}
            {percent.toFixed(1)}%
          </DeltaText>
        </Tooltip>
      )}
    </span>
  )
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function formatWeekLabel(weekStart: string): string {
  const sun = new Date(weekStart + 'T00:00:00')
  const sat = new Date(sun)
  sat.setDate(sat.getDate() + 6)
  const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  const fmtYear = sat.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  return `${fmt(sun)} – ${fmtYear}`
}

function formatMonthLabel(monthStart: string): string {
  const label = new Date(monthStart + 'T00:00:00').toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const DeltaText = styled.span<{ positive: boolean }>`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ positive }) => (positive ? '#389e0d' : '#cf1322')};
`
